import { useMemo, useState } from 'react';

import { BccBankStatus } from '@/components/Shared/constants/banks-statuses';
import { BANKS_ICONS_NAMES, BCC_PRODUCTS, DictTypes } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useAppSelector } from '@/store';

import type { TBccBankOffer } from '../../../types/bcc.types';
import useAdditionalHelpers from './useAdditionalHelpers';
import useAlternatives from './useAlternatives';

type IProps = {
  bankOffer: TBccBankOffer;
  hasOfferedResponse?: boolean;
};

const blueColoredStatuses = [
  BccBankStatus.WaitingOfFinancing,
  BccBankStatus.FinancedPledgeMissing,
  BccBankStatus.FinancedWaitingForModificationBeforePledge,
  BccBankStatus.OnRework
];

const useFormulateOfferItem = ({
  bankOffer: initialBankOffer,
  hasOfferedResponse
}: IProps) => {
  const [windowWidth] = useWindowSize();
  const isMobile = windowWidth <= 860;

  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);

  const [offerDetailsModal, setOfferDetailsModal] = useState<boolean | string>(
    false
  );

  const alternatives = useAlternatives({ bankOffer: initialBankOffer });

  const bankOffer: TBccBankOffer = useMemo(() => {
    const product =
      BCC_PRODUCTS[initialBankOffer.offeredUuid as keyof typeof BCC_PRODUCTS] ||
      orderApp?.productInfo.product.stocksBonds?.find(
        (prod) => prod.bank_name === initialBankOffer.bank.name
      )?.label ||
      'Стандарт';

    if (!alternatives?.length) {
      return {
        ...initialBankOffer,
        product
      };
    }

    const hasMainInfo =
      initialBankOffer.monthlyPayment && initialBankOffer.rate;

    const selectedAlternative =
      alternatives.find(
        (item) => item.extUuid === initialBankOffer.offeredUuid
      ) || (!hasMainInfo ? alternatives[0] : undefined);

    const filteredAlternatives = alternatives.filter(
      (item) => item.extUuid !== selectedAlternative?.extUuid
    );

    let result = {
      ...initialBankOffer,
      alternatives: filteredAlternatives,
      product
    };
    if (selectedAlternative) {
      result = {
        ...result,
        rate: selectedAlternative.rate.toString(),
        extId: selectedAlternative.extUuid,
        initialPayment: selectedAlternative.initialPayment,
        loanAmount: selectedAlternative.loanAmount,
        loanPeriod: selectedAlternative.loanPeriod.toString(),
        monthlyPayment: selectedAlternative.monthlyPayment,
        product: selectedAlternative.product || product,
        productInfo:
          typeof result.productInfo === 'object' ||
          (typeof result.productInfo === 'undefined' &&
            selectedAlternative.additionalProducts)
            ? ({
                ...(result.productInfo || {}),
                additionalProducts: selectedAlternative.additionalProducts
              } as TBccBankOffer['productInfo'])
            : result.productInfo
      };
    }

    return result;
  }, [initialBankOffer, alternatives, orderApp?.productInfo]);

  const { additionalProducts, getDescription } = useAdditionalHelpers({
    bankOffer,
    offerDetailsModal
  });

  const bccBankContracts: string = useMemo(() => {
    let contracts = '';

    if (alternatives?.length) {
      for (const item of alternatives) {
        const descriptionContracts =
          typeof item.description !== 'string'
            ? item.description?.additionalPND?.find((pnd) => pnd.contracts)
            : null;

        if (descriptionContracts) {
          contracts = JSON.stringify(descriptionContracts.contracts, null, 2);
          break;
        }
      }
    }

    return contracts;
  }, [alternatives]);

  const bankDict = dicts?.[DictTypes.Banks]?.getBy.name[bankOffer.bank.name];
  const bankIconName = BANKS_ICONS_NAMES[bankOffer.bank.name];
  const isCurrentResponseOffered = hasOfferedResponse && bankOffer.isOffered;

  return {
    additionalProducts,
    bankOffer,
    bccBankContracts,
    getDescription,
    headerIcon: bankDict?.icon || bankIconName?.icon,
    headerTitle: bankDict?.fullname || bankIconName?.name,
    isBlueColoredStatus: blueColoredStatuses.includes(
      bankOffer.status.code as BccBankStatus
    ),
    isCurrentResponseOffered,
    isMobile,
    offerDetailsModal,
    setOfferDetailsModal
  };
};

export default useFormulateOfferItem;
